
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckSquare, Plus, X } from 'lucide-react';

interface ChecklistDialogProps {
  onAddChecklist: (items: string[]) => void;
  children?: React.ReactNode;
}

export const ChecklistDialog = ({ onAddChecklist, children }: ChecklistDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<string[]>(['']);
  const [checklistTitle, setChecklistTitle] = useState('Checklist');

  const addItem = () => {
    setItems([...items, '']);
  };

  const updateItem = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    setItems(newItems);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  const handleCreate = () => {
    const validItems = items.filter(item => item.trim() !== '');
    if (validItems.length > 0) {
      const checklistText = `\n## ✅ ${checklistTitle}\n\n${validItems.map(item => `- [ ] ${item}`).join('\n')}\n`;
      onAddChecklist([checklistText]);
      setItems(['']);
      setChecklistTitle('Checklist');
      setIsOpen(false);
    }
  };

  const defaultTrigger = (
    <Button variant="ghost" className="w-full justify-start">
      <CheckSquare className="mr-2 h-4 w-4" />
      Criar Checklist
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Criar Checklist</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Título do Checklist</label>
            <Input
              value={checklistTitle}
              onChange={(e) => setChecklistTitle(e.target.value)}
              placeholder="Digite o título..."
            />
          </div>
          
          <div>
            <label className="text-sm font-medium">Itens do Checklist</label>
            <div className="space-y-2 mt-2">
              {items.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={item}
                    onChange={(e) => updateItem(index, e.target.value)}
                    placeholder={`Item ${index + 1}...`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addItem();
                      }
                    }}
                  />
                  {items.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={addItem}
              className="mt-2"
            >
              <Plus className="mr-2 h-4 w-4" />
              Adicionar Item
            </Button>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate}>
              Criar Checklist
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
